<template>
  <div class="helpcenter-view__wrapper">
    <HelpCenterNavBar />

    <main
      class="helpcenter-view__content"
      :class="{ 'helpcenter-view__content--impersonate': isImpersonate }"
    >
      <HelpCenterIncidentAlert
        v-if="renderIncidentAlert"
        :incident="incidentData"
        data-testid="incident-alert"
        class="helpcenter-view__alert"
        @onClose="handleCloseIncidentAlert"
      />

      <template v-if="isLoading">
        <SkeletonLoader
          class="helpcenter-view__content__loader"
          data-testid="loader"
        >
          <div v-for="index in 5" :key="index" class="row-loader" />
        </SkeletonLoader>
      </template>

      <template v-else-if="isFetchSectionsFailed">
        <ActionableHelperView :on-retry="fetchSections" />
      </template>

      <template v-else>
        <Input
          v-model.trim="searchInput"
          placeholder="Buscar"
          class="helpcenter-view__search-input"
          :value="searchInput"
          :right-icon-has-function="true"
          :right-icon-name="!isSearchInputEmpty ? 'ic_cancel' : '-'"
          left-icon-name="ic_search"
          :is-focus="true"
          aria-label="Procurar tópicos de ajuda"
          @input="onInputChange"
          @icon-click="clearSearch"
        />
        <template v-if="isSearching">
          <Row
            v-for="(article, index) in searchResult"
            :key="index"
            chevron
            has-interaction
            :left-title="article.title"
            @click="openArticle(article)"
          />

          <ActionableHelperView
            v-if="isFetchArticlesFailed"
            :on-retry="fetchArticles"
          />

          <HelperView
            v-else-if="isSearchResultEmpty"
            title="Nenhum artigo encontrado"
            description="Tente digitar novamente"
          />
        </template>

        <template v-else>
          <HelpCenterContentSection
            v-for="(section, sectionIndex) in sections"
            :key="sectionIndex"
            :section="section"
          />
        </template>
      </template>
    </main>

    <HelpCenterFooter
      v-if="showHelpCenterFooter"
      data-testid="help-center-footer"
    />
  </div>
</template>
<script lang="ts">
import { debounce } from 'lodash-es';
import { Component, Vue } from 'vue-property-decorator';

import {
  Row,
  HelperView,
  SkeletonLoader,
  ActionableHelperView,
  Input
} from '@warrenbrasil/nebraska-web';
import { HelpCenterNavBar } from './components/HelpCenterNavBar';
import { HelpCenterContentSection } from './components/HelpCenterContentSection';
import { HelpCenterIncidentAlert } from './components/HelpCenterIncidentAlert';
import { HelpCenterFooter } from './components/HelpCenterFooter';
import { isImpersonate } from '@/modules/common/services/partner';

import {
  searchArticles,
  getSections,
  IHelpCenterArticle,
  IHelpCenterSection
} from '@/modules/help-center/services/help-center';
import {
  getStatus,
  IIncidentStatus,
  EStatusTypes
} from '@/modules/help-center/services/incident-status';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

@Component({
  components: {
    Input,
    HelperView,
    SkeletonLoader,
    HelpCenterFooter,
    HelpCenterNavBar,
    HelpCenterContentSection,
    HelpCenterIncidentAlert,
    ActionableHelperView,
    Row
  }
})
export default class HelpCenterView extends Vue {
  isLoading = true;
  isFetchSectionsFailed = false;
  isFetchArticlesFailed = false;

  isImpersonate = false;
  sections: IHelpCenterSection[] = [];

  incidentData: IIncidentStatus = {} as IIncidentStatus;
  private incidentAlertCanBeRendered = true;

  isSearching = false;
  searchInput = '';
  searchResult: IHelpCenterArticle[] = [];

  onInputChange = debounce(this.searchArticles, 500);

  private mounted() {
    this.isImpersonate = isImpersonate();

    this.fetchSections();
    this.fetchIncidents();
  }

  get isSearchResultEmpty() {
    return this.searchResult.length === 0;
  }

  private fetchIncidents() {
    getStatus()
      .then(incidentResponse => (this.incidentData = incidentResponse))
      .catch(() => {});
  }

  async fetchArticles() {
    this.isLoading = true;
    this.isFetchArticlesFailed = false;

    try {
      const { articles } = await searchArticles(this.searchInput);
      this.searchResult = articles;
    } catch (error) {
      this.isFetchArticlesFailed = true;
      throw new Error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async fetchSections() {
    this.isLoading = true;
    this.isFetchSectionsFailed = false;

    try {
      const { sections } = await getSections();
      this.sections = sections;
    } catch (error) {
      this.isFetchSectionsFailed = true;
      throw new Error(error);
    } finally {
      this.isLoading = false;
    }
  }

  private searchArticles() {
    this.isSearching = Boolean(this.searchInput);

    if (!this.isSearchInputEmpty) this.fetchArticles();
  }

  clearSearch() {
    this.searchResult = [];
    this.isSearching = false;
    this.searchInput = '';
  }

  get renderIncidentAlert() {
    return this.isStatusIncident && this.incidentAlertCanBeRendered;
  }

  private get isStatusIncident() {
    return this.incidentData.status === EStatusTypes.Incident;
  }

  get isSearchInputEmpty() {
    return this.searchInput === '';
  }

  get showHelpCenterFooter() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.CS_CHAT);
  }

  handleCloseIncidentAlert() {
    this.incidentAlertCanBeRendered = false;
  }

  openArticle(article: IHelpCenterArticle) {
    this.$router.push({
      name: 'help-center-article',
      params: { articleId: String(article.id) }
    });
  }
}
</script>

<style lang="less" scoped>
.helpcenter-view {
  @height-navbar: 64px;

  &__alert {
    margin-bottom: @size-spacing-x600;
  }

  &__content {
    width: 100%;
    height: 100%;
    max-width: 1200px;
    min-height: calc(100vh - @height-navbar);
    margin: 0px auto;
    padding: @spacing-xxl @size-spacing-x400;

    &--impersonate {
      padding: calc(@spacing-xxl + @size-spacing-x300) @size-spacing-x400 0px;
    }
  }

  &__search-input {
    margin-bottom: @size-spacing-x800;
  }

  &__content__loader {
    padding-top: @size-spacing-x800;

    .row-loader {
      height: 30px;
      margin: @size-spacing-x500 0;
      border-radius: 10px;
    }
  }

  &__search-view__icon-wrapper {
    background: none;
    outline: none;
    width: @size-spacing-x600;
    height: @size-spacing-x600;
    border-radius: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: 0.1s;
    cursor: pointer;

    &:hover {
      background-color: @divider-primary;
    }
  }
}
</style>
